import { DateTime, Interval } from 'luxon';

export type TimePickerOption = { label: string; value: DateTime };

const TIME_FORMAT = 'hh:mm a';

function generateAutocompleteOption(dateTime: DateTime) {
  return {
    label: dateTime.toFormat(TIME_FORMAT),
    value: dateTime,
  };
}

export function buildHalfHourIntervalsForDateTime(dateTime: DateTime): TimePickerOption[] {
  const dayStart = dateTime.startOf('day');
  const dayEnd = dateTime.endOf('day');
  const intervals = [...Interval.fromDateTimes(dayStart, dayEnd).splitBy({ minutes: 30 })];
  return intervals.map((interval) => generateAutocompleteOption(interval.start));
}

// An end time can never be the first minute of the day, but it can be the following midnight.
export function buildEndTimeOptions(startTime: DateTime): TimePickerOption[] {
  const [, ...afterStartOfDay] = buildHalfHourIntervalsForDateTime(startTime);
  return [
    ...afterStartOfDay,
    generateAutocompleteOption(startTime.startOf('day').plus({ days: 1 })),
  ];
}

export function generateTimePickerOptions({
  inputValue,
  unfiltered = false,
  filterFunction,
  halfHourIntervals,
}: {
  inputValue: string;
  unfiltered: boolean;
  filterFunction?: (option: TimePickerOption) => boolean;
  halfHourIntervals: TimePickerOption[];
}) {
  const halfHourIntervalLabels = new Set(halfHourIntervals.map(({ label }) => label));
  let options = [...halfHourIntervals];

  const dateAnchor = halfHourIntervals[0]?.value;
  let parsedInputValue = DateTime.fromFormat(inputValue, TIME_FORMAT);
  if (!parsedInputValue.isValid) parsedInputValue = DateTime.fromFormat(inputValue, 'hh:mma');
  if (!parsedInputValue.isValid) parsedInputValue = DateTime.fromFormat(inputValue, 'hh:mm');
  if (!parsedInputValue.isValid) parsedInputValue = DateTime.fromFormat(inputValue, 'h:mm');
  const inputValueTime =
    dateAnchor && parsedInputValue.isValid
      ? dateAnchor.startOf('day').set({
          hour: parsedInputValue.hour,
          minute: parsedInputValue.minute,
        })
      : parsedInputValue;

  // Insert time options for arbitrary times the user types
  if (
    inputValueTime.isValid &&
    // Don't add times that already exist in the list
    !halfHourIntervalLabels.has(inputValueTime.toFormat(TIME_FORMAT))
  ) {
    options.push(generateAutocompleteOption(inputValueTime));

    const oppositeMeridianTime =
      inputValueTime.hour >= 12
        ? inputValueTime.minus({ hours: 12 })
        : inputValueTime.plus({ hours: 12 });
    options.push(generateAutocompleteOption(oppositeMeridianTime));
  }

  if (filterFunction) {
    options = options.filter(filterFunction);
  }

  // Non-half-hour intervals (custom user-typed times) come first.
  options.sort((a, b) => {
    if (!halfHourIntervalLabels.has(a.label) && halfHourIntervalLabels.has(b.label)) {
      return -1;
    }
    return 1;
  });
  if (unfiltered || !inputValue) {
    return options;
  }

  options = options.filter(
    (option) =>
      option.label.startsWith(inputValue) || option.label.replace(/^0/, '').startsWith(inputValue),
  );
  return options;
}
